import { nest, values } from "d3";
import { dateRangeToString, sortByKey } from "@/_core/_helpers/_helpers";
import { AnalyticsHelpers } from "./AnalyticsHelpers";

export class AnalyticsScenarios extends AnalyticsHelpers {
  constructor(processData, rawData, userSelections) {
    super(processData, rawData, userSelections);
  }

  /** Nest analysis scenarios */
  processDataNestAnalysisScenarios({
    _this,
    intervalGroup,
    nameData,
    analysisFieldName,
    nameQty,
    valueQty,
    filterData,
    calculateStrike = true,
    calculateDelta = false,
    ignore_field = null,
    ignore_values = [],
    calculateMinMax = false,
    calculateBalanceQty = false,
    keyStrike = "strike",
    isCustomDelta = false,
    isAverageTotal = false,
    isQtyTotals = false,
    isAbs = true,
    fieldDate = "date",
    useCustomDateProcessing = false,
    keyTotalIncludes = [],
    isRefPrice = false
  }) {
    let data;

    if (useCustomDateProcessing && Array.isArray(filterData)) {
      data = this.addDateFromField(filterData, fieldDate);
    } else if (Array.isArray(filterData)) {
      data = filterData;
      data = sortByKey(data, fieldDate);
      data = this.extendWeekData({ data, fieldDate });
    } else {
      data = _this.rawData[filterData];
      data = sortByKey(data, fieldDate);
      data = this.extendWeekData({ data, fieldDate });
    }

    const nestedData = nest()
      .key(d => this.timeComparison(d[fieldDate], intervalGroup, data))
      .rollup(v => {
        let date =
          (values(v).pop()[fieldDate] &&
            this.timeComparison(
              values(v).pop()[fieldDate],
              intervalGroup,
              data
            )) ??
          null;
        return this.createDataAnalysisScenarios({
          data: v,
          date,
          analysisFieldName,
          nameQty,
          valueQty,
          calculateStrike,
          calculateDelta,
          ignore_field,
          ignore_values,
          calculateMinMax,
          calculateBalanceQty,
          keyStrike,
          isCustomDelta,
          isAverageTotal,
          isQtyTotals,
          isAbs,
          isRefPrice
        });
      })
      .entries(data)
      .map(d => d.value);

    Object.assign(_this.rawData, {
      [nameData]: nestedData,
      [`total${nameData}`]: this.analysisScenariosTotals(
        nestedData,
        keyTotalIncludes
      )
    });

    Object.assign(_this.loading, { [nameData]: false });

    return nestedData;
  }

  getDataNestAnalysisScenarios(options) {
    return this.processDataNestAnalysisScenarios({
      ...options,
      useCustomDateProcessing: false
    });
  }

  getNestedAnalysisDataWithCustomDates(options) {
    return this.processDataNestAnalysisScenarios({
      ...options,
      useCustomDateProcessing: true
    });
  }

  /** Create all data analysis scenarios  */
  createDataAnalysisScenarios({
    data,
    date,
    analysisFieldName,
    valueQty,
    calculateStrike = true,
    keyStrike = "strike",
    calculateDelta = false,
    ignore_field = null,
    ignore_values = [],
    calculateMinMax = false,
    calculateBalanceQty = false,
    isCustomDelta = false,
    isAverageTotal = false,
    isQtyTotals = false,
    isAbs = true,
    isRefPrice = false
  }) {
    const uniqueAnalysisFieldValues = [];
    data.forEach(d => {
      if (
        d[analysisFieldName] &&
        !uniqueAnalysisFieldValues.includes(d[analysisFieldName])
      ) {
        if (ignore_field == null || d[ignore_field] === undefined) {
          uniqueAnalysisFieldValues.push(d[analysisFieldName]);
        } else {
          if (
            d[ignore_field] &&
            !ignore_values.some(str => d[ignore_field].includes(str))
          ) {
            uniqueAnalysisFieldValues.push(d[analysisFieldName]);
          }
        }
      }
    });

    if (date) {
      const analysisResults = { date };
      const changeUnit = function(value, unitChange) {
        return value != null ? unitChange * value : null;
      };
      for (const fieldValue of uniqueAnalysisFieldValues) {
        analysisResults["date"] = date;
        analysisResults["unit"] = data.filter(d => d.unit).find(d => d)?.unit;
        analysisResults["sUnit"] = data.filter(d => d.curr).find(d => d)?.curr;
        let fieldValueLowerCase = fieldValue.toLowerCase();

        const derivative2 = fieldValueLowerCase.includes("long future")
          ? "long forward"
          : fieldValueLowerCase.includes("short future")
          ? "short forward"
          : "";

        // average prices
        if (calculateStrike) {
          analysisResults[`p_${fieldValueLowerCase}`] = this.calculateStrike(
            data,
            fieldValue,
            keyStrike,
            derivative2,
            analysisFieldName
          );
        }

        // balance quantities
        if (calculateBalanceQty) {
          const findData = this.findObjectByKey(data, "total", true);
          const unit = 1 * findData?.unit_change * findData?.ratio;
          analysisResults[`q_balance`] = changeUnit(findData?.balance, unit);
        }

        // average notional - delta
        if (calculateDelta) {
          const dataAve = this.averageAnalysis({
            data,
            analysisType: fieldValue,
            multiplyField: "delta",
            isMultiplyField: true,
            calculateDelta
          });

          analysisResults[`notional_${fieldValueLowerCase}`] = dataAve[0];
          analysisResults[`delta_${fieldValueLowerCase}`] = dataAve[1];
        }

        if (isCustomDelta) {
          analysisResults[
            `delta_${fieldValueLowerCase}`
          ] = this.findObjectByKey(
            data,
            "strategy_name",
            fieldValueLowerCase
          )?.delta;

          analysisResults[`delta`] = this.findObjectByKey(
            data,
            "total",
            true
          )?.delta;
        }

        // Min Max quantities
        if (calculateMinMax) {
          // Values for total strategy
          analysisResults[`q_min`] = this.findObjectByKey(
            data,
            "total",
            true
          )?.q_min;
          analysisResults[`q_max`] = this.findObjectByKey(
            data,
            "total",
            true
          )?.q_max;

          // Values for each strategy
          analysisResults[`max_${fieldValueLowerCase}`] = this.findObjectByKey(
            data,
            "strategy_name",
            fieldValueLowerCase
          )?.q_max;
          analysisResults[`min_${fieldValueLowerCase}`] = this.findObjectByKey(
            data,
            "strategy_name",
            fieldValueLowerCase
          )?.q_min;
        }

        if (isAverageTotal) {
          analysisResults[`average`] = this.findObjectByKey(
            data,
            "total",
            true
          )?.[keyStrike];
        }

        if (isRefPrice) {
          analysisResults[`refPrice`] = this.findObjectByKey(
            data,
            "total",
            true
          )?.budget_futures;
        }

        if (isQtyTotals) {
          analysisResults[`totals`] = this.findObjectByKey(
            data,
            "total",
            true
          )?.[valueQty];
        }

        // Add quantities
        analysisResults[`q_${fieldValueLowerCase}`] = this.sumAnalysis({
          data: data.filter(d => d.total !== true),
          analysisType: fieldValue,
          valueAnalysis: valueQty,
          nameAnalysis: analysisFieldName,
          derivative2,
          isAbs
        });
      }
      return analysisResults;
    }
    return {};
  }

  getMinValueByKey(data, key) {
    return data.reduce((min, obj) => {
      const value = obj[key];
      if (value === undefined || value === null) return min;
      return min === null ? value : Math.min(min, value);
    }, null);
  }

  findObjectByKey(data, key, value) {
    return data.find(d => d[key] === value);
  }

  calculateStrike(data, fieldValue, keyStrike, derivative2, analysisFieldName) {
    if (keyStrike === "strategy_strike") {
      data = data.find(d => d[analysisFieldName] === fieldValue)?.[keyStrike];
      data = data === 0 ? null : data;
      return data;
    }

    return this.averageAnalysis({
      data,
      analysisType: fieldValue,
      multiplyField: keyStrike,
      isMultiplyField: true,
      derivative2,
      nameAnalysis: analysisFieldName
    });
  }

  /** Calculate the totals of the scenario analysis */
  analysisScenariosTotals(data, keyIncludes = [], fieldDate = "date") {
    const date =
      data.length > 0 && fieldDate in data[0]
        ? dateRangeToString(
            data[0][fieldDate],
            data[data.length - 1][fieldDate],
            true,
            "\u2192"
          )
        : "date...";

    const uniqueKeys = this.findUniqueKeys(data, keyIncludes);

    const resultPrices = this.getTotalPrices(
      data,
      uniqueKeys.filter(
        d =>
          d.startsWith("p_") ||
          keyIncludes.find(k => k.isPrice && d.startsWith(k.name))
      )
    );
    const resultQty = this.getAveragesQty(
      data,
      uniqueKeys.filter(
        d =>
          d.startsWith("q_") ||
          keyIncludes.find(k => !k.isPrice && d.startsWith(k.name))
      )
    );

    return [
      {
        date,
        ...resultPrices,
        ...resultQty
      }
    ];
  }
}
